import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  LOGO_ACCEPT,
  logoTypeError,
  serverMessage,
  trackingUrlError,
} from "./courierFields";
import TrackingApiFields from "./TrackingApiFields";

function CouriersUpdate({ data, showUpdatePage, refreshFunc }) {
  /*   All States Below
   ********************************************* */
  const { register, handleSubmit, watch } = useForm();
  const [formError, setFormError] = useState({});
  const [saving, setSaving] = useState(false);

  // The logo is only CHOSEN here; it is uploaded (or removed) when Update is
  // pressed, with the rest of the form. Until then the picked file is shown
  // as a local preview.
  const [pendingLogo, setPendingLogo] = useState(null); // File
  const [previewUrl, setPreviewUrl] = useState(null); // blob: URL of pendingLogo
  const [removeLogo, setRemoveLogo] = useState(false); // remove the saved one
  const [logoError, setLogoError] = useState(null);

  // A blob URL holds the file in memory until revoked.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  /*   All Functions Below
   ********************************************* */
  const pickLogo = (file) => {
    setLogoError(null);
    if (!file) return;
    const typeError = logoTypeError(file);
    if (typeError) {
      setLogoError(typeError);
      return;
    }
    setPendingLogo(file);
    setPreviewUrl(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const clearLogo = () => {
    setLogoError(null);
    if (pendingLogo) {
      // Undo the pick; the saved logo (if any) stays.
      setPendingLogo(null);
      setPreviewUrl(null);
      return;
    }
    setRemoveLogo(true);
  };

  // Saved by its own endpoint, multipart. The app's axios instance defaults
  // to JSON, which sent the FormData as a JSON body: the server saw no file
  // and answered 400 "Bad request" (2026-10-15). The header is set explicitly,
  // as the Product Information upload does.
  const saveLogo = async () => {
    if (pendingLogo) {
      const body = new FormData();
      body.append("file", pendingLogo);
      body.append("id", data.id);
      await API.post("/courier-service/logo", body, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
    } else if (removeLogo && data?.logo) {
      await API.post(
        "/courier-service/logo/delete",
        { id: data.id },
        { headers: authHeader() }
      );
    }
  };

  const updateCouriersData = async (formData, e) => {
    setFormError({});
    setLogoError(null);
    e.preventDefault();
    if (formData.name === "") {
      setFormError({
        name: {
          type: "name",
          message: "name is required",
        },
      });
      return;
    }
    const urlError = trackingUrlError(formData.trackingUrl);
    if (urlError) {
      setFormError({ trackingUrl: { type: "trackingUrl", message: urlError } });
      return;
    }

    setSaving(true);
    try {
      const resp = await API.patch(
        "/courier-service/",
        { data: { ...formData, id: data.id } },
        { headers: authHeader() }
      );
      if (resp.data.message.type !== "success") {
        setSaving(false);
        return;
      }
    } catch (err) {
      setSaving(false);
      alert(serverMessage(err));
      return;
    }

    try {
      await saveLogo();
    } catch (err) {
      // The name/URL are saved; keep the dialog open so the logo problem can
      // be seen and fixed rather than lost.
      setLogoError(serverMessage(err));
      setSaving(false);
      await refreshFunc();
      return;
    }

    setSaving(false);
    await refreshFunc();
    showUpdatePage(false);
  };

  // What the logo box shows: the picked file, else the saved logo unless it
  // is marked for removal.
  const showSaved = !pendingLogo && !removeLogo && data?.logo;

  return (
    <div className="row">
      <div className="col-12">
        {/*    Update FORM START
         ********************************************* */}
        {data && (
          <form
            className="ps-3 pe-3"
            onSubmit={handleSubmit(updateCouriersData)}
          >
            <div className="mb-3">
              <h3>Update {data?.name?.toUpperCase()}</h3>
            </div>
            {/* ----------  Fields  ----------- */}
            <div className="mb-3">
              <label htmlFor="name" className="form-label">
                Courier Service Name
              </label>
              <input
                className="form-control"
                type="text"
                id="name"
                defaultValue={data?.name}
                placeholder="Enter Courier Service Name"
                {...register("name")}
              />
              {formError && formError.name && (
                <p className="text-danger">{formError.name.message}</p>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="trackingUrl" className="form-label">
                Tracking URL
              </label>
              <input
                className="form-control"
                type="text"
                id="trackingUrl"
                defaultValue={data?.trackingUrl || ""}
                placeholder="https://www.ups.com/track?tracknum="
                {...register("trackingUrl")}
              />
              <small className="text-muted">
                The tracking number is added to the end of this to open the
                courier's tracking page.
              </small>
              {formError && formError.trackingUrl && (
                <p className="text-danger">{formError.trackingUrl.message}</p>
              )}
            </div>

            <TrackingApiFields register={register} watch={watch} saved={data} />

            {/* ----------  Logo (saved with Update)  ----------- */}
            <div className="mb-3">
              <label htmlFor="courier-logo" className="form-label">
                Logo
              </label>

              <div
                className="border rounded d-flex align-items-center justify-content-center bg-light mb-2"
                style={{ height: 90 }}
              >
                {pendingLogo ? (
                  <img
                    src={previewUrl}
                    alt="Selected logo preview"
                    style={{ maxHeight: 80, maxWidth: "90%", objectFit: "contain" }}
                  />
                ) : showSaved ? (
                  <div style={{ maxWidth: "90%" }}>
                    <ImageWIthSignedUrl
                      path={data.logo}
                      alt={`${data?.name || "Courier"} logo`}
                      imgStyle={{ maxHeight: 80, width: "auto", objectFit: "contain" }}
                    />
                  </div>
                ) : (
                  <span className="text-muted small">
                    {removeLogo ? "Logo will be removed when you press Update" : "No logo"}
                  </span>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                <input
                  id="courier-logo"
                  type="file"
                  className="form-control form-control-sm"
                  accept={LOGO_ACCEPT}
                  disabled={saving}
                  onChange={(e) => pickLogo(e.target.files?.[0])}
                />
                {(pendingLogo || showSaved) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger text-nowrap"
                    disabled={saving}
                    onClick={clearLogo}
                  >
                    {pendingLogo ? "Clear" : "Remove"}
                  </button>
                )}
              </div>
              <small className="text-muted">
                PNG, JPEG, WebP or GIF. Saved when you press Update.
              </small>
              {logoError && <p className="text-danger mb-0">{logoError}</p>}
            </div>

            <div className={`mt-4 mb-2 d-flex justify-content-end`}>
              <div>
                <button
                  className="btn px-1 btn-secondary"
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    showUpdatePage(false);
                  }}
                >
                  Cancel
                </button>
                <button className="btn btn-primary ms-1" type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Update"}
                </button>
              </div>
            </div>
          </form>
        )}
        {/*    Update FORM END
         ********************************************* */}
      </div>
    </div>
  );
}

export default CouriersUpdate;
